// Tela de planos (P0.7 + funil de vendas): Mensal ou Anual (o anual vem marcado — AC-209),
// com os preços do Stripe na moeda que o Stripe Checkout vai mostrar para a localização da
// pessoa (AC-110 / AC-111). Stripe Checkout: nenhum dado de cartão passa pelo app.
// Enquanto aberta, consulta a assinatura a cada 5 s. Sem teste grátis: quem nunca assinou vê o
// preço da 1ª cobrança com 20% de desconto, o preço cheio depois e a garantia (AC-233).
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { annualDiscount, currencyFor, guaranteeDays, PLANS, priceLabel, specPricing, type Plan, type Pricing } from '../domain/access';
import { useStore } from '../state/AppStore';
import { useServices } from '../state/services';
import { useAccess, useSubscriptionPolling } from '../state/useSubscription';
import { HoneyDrip } from './HoneyDrip';
import { BeeLogo, Icon } from './Icon';
import { cls } from './NewTaskForm';

function systemLocales(): readonly string[] {
  if (typeof navigator === 'undefined') return ['en-US'];
  return navigator.languages?.length ? navigator.languages : [navigator.language];
}

export function Paywall({ onBack }: { onBack(): void }) {
  const { t } = useTranslation();
  const store = useStore();
  const services = useServices();
  const access = useAccess();
  const [busy, setBusy] = useState(false);
  // o mel cobre do topo da janela até a base do logo (AC-237)
  const pageRef = useRef<HTMLElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const [honey, setHoney] = useState({ top: 0, left: 0, width: 0, height: 0 });
  useLayoutEffect(() => {
    const measure = () => {
      const page = pageRef.current?.getBoundingClientRect();
      const logo = logoRef.current?.getBoundingClientRect();
      if (!page || !logo) return;
      setHoney({ top: -page.top, left: -page.left, width: document.documentElement.clientWidth, height: Math.round(logo.bottom) });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  const [plan, setPlan] = useState<Plan>('monthly'); // o mensal vem escolhido (foco do usuário em 03/11/2026)
  useSubscriptionPolling(true);
  const deviceCurrency = currencyFor(systemLocales());
  // null = ainda perguntando ao Stripe; sem resposta, os valores da spec
  const [pricing, setPricing] = useState<Pricing | null>(null);
  useEffect(() => {
    let alive = true;
    store.backend.subscription
      .prices(deviceCurrency)
      .then((p) => alive && setPricing(p))
      .catch(() => alive && setPricing(specPricing(deviceCurrency)));
    return () => {
      alive = false;
    };
  }, [store, deviceCurrency]);
  const annual = plan === 'annual';
  // o preço mostrado é o cobrado: sem cupom de primeira assinatura (decisão de 03/11/2026)
  const charge = pricing ? (annual ? pricing.annual : pricing.monthly) : 0;
  const period = annual ? t('paywall.perYear') : t('paywall.perMonth');

  const subscribe = async () => {
    setBusy(true);
    try {
      const url = await store.backend.subscription.checkoutUrl(plan);
      await services.openExternal(url);
    } catch {
      store.toast('errors.checkout');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="paywall" data-testid="paywall" ref={pageRef}>
      <HoneyDrip {...honey} />
      <button type="button" className="back" onClick={onBack}>
        <Icon name="chevronLeft" size={18} />
        <span>{access.hasAccess ? t('common.back') : t('paywall.seeTasks')}</span>
      </button>
      <div className="paywall__logo" ref={logoRef}>
        <BeeLogo size={72} />
      </div>
      {access.paying ? (
        <>
          <h1 className="paywall__title">{t('paywall.activeTitle')}</h1>
          <p className="paywall__sub">{t('paywall.activeBody')}</p>
        </>
      ) : (
        <>
          <h1 className="paywall__title">{access.subscriptionEnded ? t('paywall.endedTitle') : t('paywall.title')}</h1>
          <p className="paywall__sub">{access.subscriptionEnded ? t('paywall.endedSub') : t('paywall.note')}</p>
          <div className="segmented plan-switch" role="radiogroup" aria-label={t('paywall.planLabel')}>
            {PLANS.map((p) => (
              <button key={p} type="button" role="radio" aria-checked={plan === p} className={cls('segmented__item', plan === p && 'is-on')} onClick={() => setPlan(p)}>
                {t(`paywall.plan.${p}`)}
                {p === 'annual' && pricing && annualDiscount(pricing) > 0 && <span className="plan-switch__badge">-{annualDiscount(pricing)}%</span>}
              </button>
            ))}
          </div>
          {pricing ? (
            <>
              <div className="price" key={plan}>
                <span className="price__value" data-testid="price">
                  {priceLabel(charge, pricing.currency)}
                </span>
                <span className="price__period">{period}</span>
              </div>
              {annual && <p className="price__per-month">{t('paywall.annualPerMonth', { price: priceLabel(Math.round((charge / 12) * 100) / 100, pricing.currency) })}</p>}
            </>
          ) : (
            <div className="price price--loading" aria-busy="true" aria-label={t('common.loading')} />
          )}
          <ul className="paywall__features">
            {(['float', 'order', 'timer', 'pomodoro', 'recurring', 'sync'] as const).map((k) => (
              <li key={k}>
                <Icon name="check" size={14} />
                {t(`paywall.features.${k}`)}
              </li>
            ))}
          </ul>
          <button type="button" className="btn btn--cta" disabled={busy || !store.get().online} onClick={() => void subscribe()}>
            {t('paywall.subscribe')}
          </button>
          {pricing && (
            <div className="paywall__guarantee" data-testid="guarantee">
              <span>
                <Icon name="check" size={14} />
                {t('paywall.guarantee', { count: guaranteeDays(pricing.currency) })}
              </span>
              <span>{t('paywall.secure')}</span>
            </div>
          )}
          <p className="paywall__note">
            <Icon name="lock" size={12} />
            {t('paywall.note')}
          </p>
        </>
      )}
    </section>
  );
}
